import {
  CompositeFilterDescriptor,
  DataResult,
  FilterOperator,
  State,
} from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../common/widgets/Button';
import { DataLoader } from '../../../../common/widgets/DataLoader';
import Grid, { GridCellTd } from '../../../../common/widgets/Grid';
import Icons from '../../../../common/widgets/Icons';
import Input from '../../../../common/widgets/Input';
import Loader from '../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../common/widgets/LoadingSpinner';
import { config } from '../../config';
import { NotificationItemModel } from '../../models/notifications';
import { resources } from '../../resources/resources';
import { notificationService } from '../../services/notifications';
import { EmptyStateNoNotifications } from '../Common/EmptyStates';

const StateCell = ({ dataItem }: GridCellProps) => {
  const navigate = useNavigate();
  return (
    <GridCellTd>
      <span onClick={() => navigate(`${dataItem.notificationId}`)}>
        {dataItem.wasRead ? (
          <Icons.NotificationsRead />
        ) : (
          <Icons.NotificationsNonRead />
        )}
      </span>
    </GridCellTd>
  );
};

const TitleCell = ({ dataItem }: GridCellProps) => {
  const navigate = useNavigate();
  return (
    <GridCellTd className="m-msg-grid-title">
      <span
        onClick={() => navigate(`${dataItem.notificationId}`)}
        dangerouslySetInnerHTML={{
          __html: dataItem.title ?? <p></p>,
        }}
      ></span>
    </GridCellTd>
  );
};

const DateSendCell = ({ dataItem }: GridCellProps) => {
  const navigate = useNavigate();
  return (
    <GridCellTd className="m-msg-grid-date">
      <span onClick={() => navigate(`${dataItem.notificationId}`)}>
        {moment(dataItem.dateSend as Date).format('DD.MM.yyyy, hh:mm')}
      </span>
    </GridCellTd>
  );
};

const NotificationArrow = ({ dataItem }: GridCellProps) => {
  const navigate = useNavigate();
  return (
    <GridCellTd className="m-msg-grid-arrow">
      <span onClick={() => navigate(`${dataItem.notificationId}`)}>
        <Icons.MNotificationArrow />
      </span>
    </GridCellTd>
  );
};

const initialDataState: State = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'dateSend', dir: 'desc' }],
};

interface NotificationListState {
  notifications: {
    data: NotificationItemModel[];
    dataState: State;
    total: number;
  };
  searchByTitleCrt: string;
  isSearchInputVisible: boolean;
  displayOnlyNonRead: boolean;
  refresh: boolean;
}

export const NotificationList = () => {
  const { t: translate } = useTranslation();
  const [state, setState] = useState<NotificationListState>({
    notifications: { dataState: initialDataState },
    searchByTitleCrt: '',
    isSearchInputVisible: false,
    displayOnlyNonRead: false,
  } as NotificationListState);

  const getFilters = useCallback(
    (
      searchByTitleCrt: string,
      displayOnlyNonRead: boolean,
    ): CompositeFilterDescriptor => {
      const filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
      if (searchByTitleCrt !== '') {
        filter.filters.push({
          field: 'title',
          operator: FilterOperator.Contains,
          value: searchByTitleCrt,
        });
      }
      if (displayOnlyNonRead) {
        filter.filters.push({
          field: 'wasRead',
          operator: FilterOperator.EqualTo,
          value: false,
        });
      }
      return filter;
    },
    [],
  );

  const handleSearchByTitleCrtChanged = (value: string) => {
    setState(
      produce(d => {
        d.searchByTitleCrt = value;
        d.notifications.dataState.filter = getFilters(
          value,
          state.displayOnlyNonRead,
        );
      }),
    );
  };

  const handleAllClicked = () => {
    setState(
      produce(d => {
        d.displayOnlyNonRead = false;
        d.notifications.dataState.filter = getFilters(
          state.searchByTitleCrt,
          false,
        );
      }),
    );
  };

  const handleNonReadClicked = () => {
    setState(
      produce(d => {
        d.displayOnlyNonRead = true;
        d.notifications.dataState.filter = getFilters(
          state.searchByTitleCrt,
          true,
        );
      }),
    );
  };

  const onDataReceived = (result: DataResult) => {
    setState(
      produce(d => {
        d.notifications.data = result.data as NotificationItemModel[];
        d.notifications.total = result.total;
      }),
    );
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setState(
      produce(d => {
        d.notifications.dataState = e.dataState;
      }),
    );
  };

  return (
    <>
      <div className="m-page-actions">
        <h1>{translate(resources.notifications.title)}</h1>
      </div>
      <div className="m-panel">
        <div className="m-data-utility">
          <ul className="m-data-filters">
            <li>
              <span
                onClick={handleAllClicked}
                className={`${
                  state.displayOnlyNonRead ? '' : 'm-data-active-filter'
                }`}
              >
                {translate(resources.notifications.filters.all)}
              </span>
            </li>
            <li>
              <span
                onClick={handleNonReadClicked}
                className={`${
                  state.displayOnlyNonRead ? 'm-data-active-filter' : ''
                }`}
              >
                {translate(resources.notifications.filters.nonRead)}
              </span>
            </li>
          </ul>
          <div className="m-data-search">
            {state.isSearchInputVisible && (
              <div className="m-data-search-input">
                <Input
                  type="text"
                  placeholder={translate(
                    resources.notifications.filters.search.placeholder,
                  )}
                  value={state.searchByTitleCrt}
                  onChange={e => handleSearchByTitleCrtChanged(e.value)}
                />
                <Button
                  onClick={() => {
                    setState(
                      produce(d => {
                        d.isSearchInputVisible = false;
                      }),
                    );
                  }}
                  icon={<Icons.MDataSearch />}
                />
              </div>
            )}
            <Button
              className="m-data-search-opener"
              onClick={() => {
                setState(
                  produce(d => {
                    d.isSearchInputVisible = true;
                    d.searchByTitleCrt = '';
                  }),
                );
                handleSearchByTitleCrtChanged('');
              }}
              icon={
                state.isSearchInputVisible ? (
                  <Icons.MDataCloseSearch />
                ) : (
                  <Icons.MDataSearch />
                )
              }
            ></Button>
          </div>
        </div>
        <DataLoader
          getData={notificationService.getNotifications}
          dataState={state.notifications.dataState}
          onDataReceived={onDataReceived}
        />
        <Loader loading={!state.notifications.data} loader={<LoadingSpinner />}>
          {state.notifications?.data?.length ? (
            <div className="m-grid m-messages-grid">
              <Grid
                data={state.notifications.data}
                hideHeaderRow
                pageable={{
                  pageSizes: config.grid.pageSizes,
                  pageSizeValue: state.notifications.dataState.take,
                  info: false,
                  previousNext: true,
                }}
                {...state.notifications.dataState}
                onDataStateChange={onDataStateChange}
                total={state.notifications.total}
                columns={[
                  {
                    field: 'wasRead',
                    cell: StateCell,
                  },
                  {
                    field: 'title',
                    cell: TitleCell,
                  },
                  {
                    field: 'dateSend',
                    cell: DateSendCell,
                  },
                  {
                    field: 'NotificationArrow',
                    cell: NotificationArrow,
                  },
                ]}
              />
            </div>
          ) : (
            <EmptyStateNoNotifications />
          )}
        </Loader>
      </div>
    </>
  );
};
